import { SupabaseClient } from '@supabase/supabase-js';
import { supabase as supabaseClient } from '../client/browser';
import { v4 as uuidv4 } from 'uuid';

export interface HealthRecordData {
  id?: string;
  userId: string;
  dateOpened?: string;
  dateClosed?: string;
  retentionUntil?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'CLOSED' | 'ARCHIVED';
  privacyDirectives?: any;
}

/**
 * Health Record service for managing client health records
 */
export const healthRecordService = {
  /**
   * Get health record by user ID
   */
  async getByUserId(userId: string, client?: SupabaseClient): Promise<HealthRecordData | null> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('health_records')
      .select('*')
      .eq('userId', userId)
      .single();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
      console.error('Error fetching health record:', error);
      throw error;
    }
    
    return data;
  },

  /**
   * Get active health record for a user
   */
  async getActiveByUserId(userId: string, client?: SupabaseClient): Promise<HealthRecordData | null> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('health_records')
      .select('*')
      .eq('userId', userId)
      .eq('status', 'ACTIVE')
      .single();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching active health record:', error);
      throw error;
    }
    
    return data;
  },

  /**
   * Create a new health record
   */
  async create(userId: string, client?: SupabaseClient): Promise<string> {
    const supabase = client || supabaseClient;
    
    // Get the current date as YYYY-MM-DD string in user's local timezone
    const today = new Date();
    const dateString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const { data, error } = await supabase
      .from('health_records')
      .insert({
        id: uuidv4(),
        userId: userId,
        dateOpened: dateString,  // Send as date string, no timezone issues
        status: 'ACTIVE',
        createdAt: new Date(),
        updatedAt: new Date()
      })
      .select('id')
      .single();
    
    if (error) {
      console.error('Error creating health record:', error);
      throw error;
    }
    
    return data.id;
  },

  /**
   * Reactivate an existing health record
   */
  async reactivate(healthRecordId: string, client?: SupabaseClient): Promise<void> {
    const supabase = client || supabaseClient;
    
    const { error } = await supabase
      .from('health_records')
      .update({
        status: 'ACTIVE',
        updatedAt: new Date()
      })
      .eq('id', healthRecordId);
    
    if (error) {
      console.error('Error reactivating health record:', error);
      throw error;
    }
  },

  /**
   * Ensure a health record exists for the user, creating or reactivating as needed
   */
  async ensure(userId: string, client?: SupabaseClient): Promise<string> {
    const supabase = client || supabaseClient;
    
    // Check for any existing health record (active or inactive)
    const { data: existingHealthRecord } = await supabase
      .from('health_records')
      .select('id, status')
      .eq('userId', userId)
      .single();
    
    if (!existingHealthRecord) {
      // No health record exists, create new one
      return await this.create(userId, supabase);
    }
    
    // If the existing record is not ACTIVE, reactivate it
    if (existingHealthRecord.status !== 'ACTIVE') {
      await this.reactivate(existingHealthRecord.id, supabase);
    }
    
    return existingHealthRecord.id;
  },

  /**
   * Close a health record
   */
  async close(healthRecordId: string, client?: SupabaseClient): Promise<void> {
    const supabase = client || supabaseClient;
    
    const now = new Date();
    const retentionDate = new Date(now);
    retentionDate.setFullYear(retentionDate.getFullYear() + 10); // 10 year retention
    
    // Convert dates to YYYY-MM-DD strings for date-only fields
    const dateString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const retentionString = `${retentionDate.getFullYear()}-${String(retentionDate.getMonth() + 1).padStart(2, '0')}-${String(retentionDate.getDate()).padStart(2, '0')}`;
    
    const { error } = await supabase
      .from('health_records')
      .update({
        status: 'CLOSED',
        dateClosed: dateString,
        retentionUntil: retentionString,
        updatedAt: now
      })
      .eq('id', healthRecordId);
    
    if (error) {
      console.error('Error closing health record:', error);
      throw error;
    }
  },

  /**
   * Archive a health record
   */
  async archive(healthRecordId: string, client?: SupabaseClient): Promise<void> {
    const supabase = client || supabaseClient;
    
    const { error } = await supabase
      .from('health_records')
      .update({
        status: 'ARCHIVED',
        updatedAt: new Date()
      })
      .eq('id', healthRecordId);
    
    if (error) {
      console.error('Error archiving health record:', error);
      throw error;
    }
  },

  /**
   * Update privacy directives for a health record
   */
  async updatePrivacyDirectives(
    healthRecordId: string,
    privacyDirectives: any,
    client?: SupabaseClient
  ): Promise<void> {
    const supabase = client || supabaseClient;
    
    const { error } = await supabase
      .from('health_records')
      .update({
        privacyDirectives,
        updatedAt: new Date()
      })
      .eq('id', healthRecordId);
    
    if (error) {
      console.error('Error updating privacy directives:', error);
      throw error;
    }
  }
};